/**
 * OMMO scroll behaviors — site-wide (replaces EC-CUBE scroll.js)
 *
 * Markup conventions:
 *   .tO / .bb              → add .inview on scroll into view
 *   .inview_box            → toggle .inview_box_in on data-style target
 *   .area_color            → toggle data-color class on header
 *   .inviewImg             → lazy-load from data-src
 *   .scroll_box_up         → parallax translateY
 */
(function () {
  'use strict';

  const HEADER_SELECTOR = '.ec-layoutRole__header';

  function inViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Observe elements; callback runs on every intersection change.
   * Elements already in the viewport are revealed immediately.
   */
  function observe(selector, callback, options) {
    const nodes = document.querySelectorAll(selector);
    if (!nodes.length) return;

    const settings = Object.assign(
      { root: null, rootMargin: '0px', threshold: 0 },
      options || {}
    );

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) {
        callback({ target: el, isIntersecting: true });
      });
      return;
    }

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(callback);
    }, settings);

    nodes.forEach(function (el) {
      if (inViewport(el)) {
        callback({ target: el, isIntersecting: true });
      }
      observer.observe(el);
    });
  }

  function revealOnEnter(selector) {
    observe(selector, function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('inview');
      }
    });
  }

  function toggleOnIntersect(selector, onEnter, onLeave, options) {
    observe(
      selector,
      function (entry) {
        if (entry.isIntersecting) {
          onEnter(entry);
        } else if (onLeave) {
          onLeave(entry);
        }
      },
      options
    );
  }

  function initReveal() {
    if (prefersReducedMotion()) {
      document.querySelectorAll('.tO, .bb').forEach(function (el) {
        el.classList.add('inview');
      });
      return;
    }

    revealOnEnter('.tO');
    revealOnEnter('.bb');
  }

  function initInviewBox() {
    toggleOnIntersect(
      '.inview_box',
      function (entry) {
        var name = entry.target.dataset.style;
        if (!name) return;
        var target = document.querySelector('.' + name);
        if (target) target.classList.add('inview_box_in');
      },
      function (entry) {
        var name = entry.target.dataset.style;
        if (!name) return;
        var target = document.querySelector('.' + name);
        if (target) target.classList.remove('inview_box_in');
      }
    );
  }

  function initAreaColor() {
    var header = document.querySelector(HEADER_SELECTOR);
    if (!header) return;

    var COLOR_CLASSES = ['white_base', 'black_base'];
    var AREA_MARGIN_RATIO = 0.3;

    function isVisible(el) {
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    }

    function intersectsActiveZone(el) {
      var rect = el.getBoundingClientRect();
      var viewportHeight = window.innerHeight;
      var margin = viewportHeight * AREA_MARGIN_RATIO;

      return rect.bottom > margin && rect.top < viewportHeight - margin;
    }

    function syncHeaderColor() {
      var activeColor = null;
      var activeTop = Infinity;

      document.querySelectorAll('.area_color').forEach(function (el) {
        if (!isVisible(el) || !intersectsActiveZone(el)) return;

        var top = el.getBoundingClientRect().top;
        if (top < activeTop) {
          activeTop = top;
          activeColor = el.dataset.color;
        }
      });

      header.classList.remove.apply(header.classList, COLOR_CLASSES);
      if (activeColor) {
        header.classList.add(activeColor);
      }
    }

    var nodes = document.querySelectorAll('.area_color');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      syncHeaderColor();
      window.addEventListener('scroll', syncHeaderColor, { passive: true });
      window.addEventListener('resize', syncHeaderColor, { passive: true });
      return;
    }

    var observer = new IntersectionObserver(
      function () {
        syncHeaderColor();
      },
      { root: null, rootMargin: '-30% 0px', threshold: 0 }
    );

    nodes.forEach(function (el) {
      observer.observe(el);
    });

    syncHeaderColor();
  }

  function initLazyImages() {
    observe(
      '.inviewImg',
      function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var src = el.dataset.src;
        // EC-CUBE lookbooks use a placeholder in src and the real image in data-src.
        if (src && el.getAttribute('src') !== src) {
          el.setAttribute('src', src);
          el.removeAttribute('data-src');
        }
      },
      { rootMargin: '80% 0px' }
    );
  }

  function initParallax() {
    var nodes = document.querySelectorAll('.scroll_box_up');
    if (!nodes.length || prefersReducedMotion()) return;

    var ticking = false;

    function update() {
      nodes.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var y = rect.top / 10;
        el.style.transform =
          'matrix3d(1,0,0,0, 0,1,0,0, 0,0,1,0, 0,' + y + ',0,1)';
      });
      ticking = false;
    }

    function schedule() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  }

  function init() {
    initReveal();
    initInviewBox();
    initAreaColor();
    initLazyImages();
    initParallax();
    document.documentElement.classList.add('ommo-scroll-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
